import { useRef, useCallback } from 'react';

export function useSnapScroll() {
  const scrollNodeRef = useRef<HTMLDivElement>();
  const onScrollRef = useRef<() => void>();
  const observerRef = useRef<ResizeObserver>();
  const lastScrollPosition = useRef<number>(0);
  const scrollTimeoutRef = useRef<number>();
  const isScrollingRef = useRef(false);

  const smoothScrollTo = useCallback((element: HTMLDivElement, target: number, duration = 300) => {
    const start = element.scrollTop;
    const change = target - start;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smoother animation
      const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      const currentProgress = easeInOutCubic(progress);
      element.scrollTop = start + change * currentProgress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        isScrollingRef.current = false;
      }
    };

    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
      requestAnimationFrame(animateScroll);
    }
  }, []);

  const messageRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const observer = new ResizeObserver(() => {
          if (scrollNodeRef.current) {
            // Clear any existing scroll timeout
            if (scrollTimeoutRef.current) {
              window.clearTimeout(scrollTimeoutRef.current);
            }

            // Debounce the scroll update
            scrollTimeoutRef.current = window.setTimeout(() => {
              const { scrollHeight, clientHeight } = scrollNodeRef.current!;
              const scrollTarget = scrollHeight - clientHeight;

              smoothScrollTo(scrollNodeRef.current!, scrollTarget);
            }, 100); // Adjust this delay as needed
          }
        });

        observer.observe(node);
        observerRef.current = observer;
      } else {
        observerRef.current?.disconnect();
        observerRef.current = undefined;

        if (scrollTimeoutRef.current) {
          window.clearTimeout(scrollTimeoutRef.current);
        }
      }
    },
    [smoothScrollTo],
  );

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      onScrollRef.current = () => {
        const { scrollTop } = node;
        lastScrollPosition.current = scrollTop;
      };

      node.addEventListener('scroll', onScrollRef.current);
      scrollNodeRef.current = node;
    } else {
      if (onScrollRef.current) {
        scrollNodeRef.current?.removeEventListener('scroll', onScrollRef.current);
      }

      scrollNodeRef.current = undefined;
      onScrollRef.current = undefined;
    }
  }, []);

  return [messageRef, scrollRef];
}
